/**
 * Vite browser entry point for `@shn/phg-client-surface-one`.
 *
 * Mounts `<SurfaceOneApp />` into `#root` when running in a browser
 * context. Mirrors the `@shn/phg-client-web` `index.tsx` mount
 * pattern; node-targeted tests don't trigger DOM mounting (tests
 * import `<SurfaceOneApp />` directly via `@testing-library/react`).
 *
 * Root cached on `window.__surfaceOneRoot` so Vite HMR re-execution
 * of this module re-renders into the existing root rather than
 * calling `createRoot` again on the same container — eliminates the
 * "calling createRoot on a container that has already been passed to
 * createRoot before" warning spam in dev (HMR-only; production builds
 * execute this module once). Issue #266 Finding 3.
 */

import * as React from 'react';
import { createRoot, type Root } from 'react-dom/client';

import { SurfaceOneApp } from './surface-one-app.js';

declare global {
  interface Window {
    __surfaceOneRoot?: Root;
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const rootEl = document.getElementById('root');
  if (rootEl !== null) {
    const root = window.__surfaceOneRoot ?? createRoot(rootEl);
    window.__surfaceOneRoot = root;
    root.render(
      <React.StrictMode>
        <SurfaceOneApp />
      </React.StrictMode>
    );
  }
}
